import React, { useEffect, useState, useMemo, useRef } from 'react';
import Joyride, { STATUS } from 'react-joyride';
import { useAuth } from './hooks/useAuth';
import { useChat } from './hooks/useChat';
import { usePWAInstall } from './hooks/usePWAInstall';
import { useNotifications } from './hooks/useNotifications';
import { useNavigation, VIEWS } from './hooks/useNavigation';
import { useOnboardingFlow } from './hooks/useOnboardingFlow';
import stateManager from './services/stateManager';
import * as mixpanelService from './services/mixpanelService';
import LoginForm from './components/LoginForm';
import OnboardingScreen from './components/OnboardingScreen';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import NotificationPermissionModal from './components/NotificationPermissionModal';
import Header from './components/Header';
import ChatArea from './components/ChatArea';
import InputSection from './components/InputSection';
import TodoList from './components/TodoList';
import ProgressBar from './components/ProgressBar';
import TodoCompletionModal from './components/TodoCompletionModal';
import HamburgerMenu from './components/HamburgerMenu';
import FeedbackModal from './components/FeedbackModal';
import FeedbackButton from './components/FeedbackButton';
import NotificationToast from './components/NotificationToast';
import ArticleDetailView from './components/ArticleDetailView';
import NewsDetailView from './components/NewsDetailView';
import AllLearningResourcesView from './components/AllLearningResourcesView';
import AllNewsView from './components/AllNewsView';
import ProgressDashboard from './components/ProgressDashboard';
import QueuedFactModal from './components/QueuedFactModal';
import SessionBanner from './components/SessionBanner';
import SessionDetailsModal from './components/SessionDetailsModal';
import SchedulingScreen from './components/SchedulingScreen';
import CoachDashboard from './components/CoachDashboard';
import OrganizationDashboard from './components/OrganizationDashboard';
import DevDashboard from './components/DevDashboard';
import SettingsScreen from './components/SettingsScreen';
import UpdateNotification from './components/UpdateNotification';
import { todosAPI, sessionsAPI, contentAPI } from './services/api';
import styles from './styles/App.module.css';

// Helper function to format session time for display
function formatSessionTime(isoString) {
  const date = new Date(isoString);
  const day = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} at ${time}`;
}

function App() {
  const { user, login, logout, updateUser, isLoading: authLoading } = useAuth();
  const {
    messages,
    isLoading: chatLoading,
    conversationId,
    currentMission,
    loadingMessage,
    sendMessage,
    loadHistory
  } = useChat();
  const { isInstallable, promptInstall, dismissPrompt, isIOS } = usePWAInstall();
  const {
    requestPermission: requestNotificationPermission,
    showBanner: showNotificationBanner,
    dismissBanner: dismissNotificationBanner,
    inAppNotification,
    clearInAppNotification
  } = useNotifications(messages, user);
  const { currentView, viewData, navigateTo, goBack } = useNavigation(VIEWS.CHAT);
  const {
    shouldShowOnboarding,
    flowItems,
    flowType,
    startOnboarding,
    completeOnboarding
  } = useOnboardingFlow(user);


  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [justSwitchedFromTodos, setJustSwitchedFromTodos] = useState(false);
  const [hasSeenCompletionModal, setHasSeenCompletionModal] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [readingSession, setReadingSession] = useState(null); // Track content reading { contentType, title, startTime }
  const [visitingScreen, setVisitingScreen] = useState(null); // Track screen visits for context { screenType, startTime, data }
  const [showQueuedFactModal, setShowQueuedFactModal] = useState(false); // Disabled temporarily to debug loading screen
  const [hasQueuedFact, setHasQueuedFact] = useState(false); // Track if queued fact is showing
  const [progressBarKey, setProgressBarKey] = useState(0); // Force ProgressBar remount on objective change
  const [hideProgressBar, setHideProgressBar] = useState(false); // Hide during objective change to prevent stale data
  const [upcomingSession, setUpcomingSession] = useState(null); // Track upcoming coaching session for banner
  const [selectedCoachForScheduling, setSelectedCoachForScheduling] = useState(null); // Coach selected for scheduling
  const [showSessionDetails, setShowSessionDetails] = useState(false); // Show session details modal
  const [canJoinSession, setCanJoinSession] = useState(false); // Track if session is joinable

  // Onboarding tour state
  const [runTour, setRunTour] = useState(false);

  // Build tour steps dynamically based on what's available
  const tourSteps = useMemo(() => {
    const steps = [
    {
      target: '[data-tour="input-section"]',
      content: (
        <>
          <h3>Chat with Sophia</h3>
          <p>Ask me anything about personal finance, investing, or your financial goals.</p>
        </>
      ),
      placement: 'top',
      disableBeacon: true,
    },
    {
      target: '[data-tour="progress-bar"]',
      content: (
        <>
          <h3>Your Action Plan</h3>
          <p>Track your progress through personalised financial milestones. Tap to see all your tasks.</p>
        </>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="hamburger-menu"]',
      content: (
        <>
          <h3>Navigation Menu</h3>
          <p>Access your action plan, progress dashboard, learning resources, and more. Tap to open the menu.</p>
        </>
      ),
      placement: 'bottom-start',
      disableBeacon: true,
    },
    {
      target: '[data-tour="feedback-button"]',
      content: (
        <>
          <h3>Report Issues</h3>
          <p>Found a bug or have feedback? Use this button on the left to let us know.</p>
        </>
      ),
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tour="enable-notifications"]',
      content: (
        <>
          <h3>Enable Notifications</h3>
          <p>Stay on track with personalized reminders. Tap here to enable notifications and never miss important updates about your financial journey.</p>
        </>
      ),
      placement: 'left',
      disableBeacon: true,
    },
    ];

    // Only add install step if PWA is installable or on iOS
    if (isInstallable || isIOS) {
      steps.push({
        target: '[data-tour="install-app"]',
        content: (
          <>
            <h3>Install as App</h3>
            <p>Install Sophia on your device for quick access and a native app experience. Works offline too!</p>
          </>
        ),
        placement: 'left',
        disableBeacon: true,
      });
    }

    return steps;
  }, [isInstallable, isIOS]);

  // 1 second startup delay to avoid using resources during testing
  const [startupDelay, setStartupDelay] = useState(true);

  // Refs to prevent duplicate Mixpanel tracking
  const hasTrackedAppOpened = useRef(false);
  const lastTrackedView = useRef(null);

  // Check if there's a pending plan (TodoPlan tag in last message)
  // Memoized to avoid recalculating on every render
  const hasPendingPlan = useMemo(() => {
    return messages.length > 0 &&
      messages[messages.length - 1].content?.includes('<TodoPlan:');
  }, [messages]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setStartupDelay(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Fetch upcoming coaching session for banner
  useEffect(() => {
    const fetchUpcomingSession = async () => {
      if (!user) {
        console.log('[App] Skipping session fetch - no user');
        return;
      }
      try {
        console.log('[App] Fetching upcoming session...');
        const response = await sessionsAPI.getNextSession();
        const nextSession = response?.session;
        console.log('[App] Next session response:', nextSession);
        if (nextSession) {
          // Use server-calculated minutesRemaining to avoid timezone issues
          const minutesRemaining = nextSession.minutesRemaining || 0;
          console.log('[App] Minutes remaining (from server):', minutesRemaining);

          if (minutesRemaining > -10) {
            console.log('[App] Setting upcoming session');
            setUpcomingSession(nextSession);
          } else {
            console.log('[App] Session too far in past, clearing');
            setUpcomingSession(null);
          }
        } else {
          console.log('[App] No session found, clearing');
          setUpcomingSession(null);
        }
      } catch (err) {
        console.error('[App] Failed to fetch upcoming session:', err);
      }
    };

    if (!startupDelay) {
      fetchUpcomingSession();
      // Refresh every 5 minutes
      const interval = setInterval(fetchUpcomingSession, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [user, startupDelay]);

  // Auto-start tour for first-time users
  useEffect(() => {
    if (!user || shouldShowOnboarding || startupDelay) return;

    const tourCompleted = localStorage.getItem('sophia_tour_completed');

    // Only start tour when all required elements are likely to be present:
    // - Chat view is active
    // - Chat is not loading
    // - We have a conversationId (so ProgressBar can render)
    // - No pending plan (so InputSection can render)
    // - Messages are loaded (so chat is ready)
    if (!tourCompleted &&
        currentView === VIEWS.CHAT &&
        !chatLoading &&
        conversationId &&
        !hasPendingPlan &&
        messages.length > 0) {
      // Verify all tour target elements are in the DOM before starting
      let retryCount = 0;
      const maxRetries = 5; // Try for up to 5 seconds (5 retries * 1 second)

      const checkElementsAndStartTour = () => {
        const inputSection = document.querySelector('[data-tour="input-section"]');
        const progressBar = document.querySelector('[data-tour="progress-bar"]');
        const hamburgerMenu = document.querySelector('[data-tour="hamburger-menu"]');
        const feedbackButton = document.querySelector('[data-tour="feedback-button"]');

        if (inputSection && progressBar && hamburgerMenu && feedbackButton) {
          console.log('[Tour] All tour elements found in DOM, starting tour');
          setRunTour(true);
        } else {
          console.log('[Tour] Some elements missing (retry', retryCount + 1, '/', maxRetries, '):', {
            inputSection: !!inputSection,
            progressBar: !!progressBar,
            hamburgerMenu: !!hamburgerMenu,
            feedbackButton: !!feedbackButton
          });

          // Retry if we haven't exceeded max retries
          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(checkElementsAndStartTour, 1000);
          } else {
            console.warn('[Tour] Failed to find all elements after', maxRetries, 'retries. Tour will not start automatically.');
          }
        }
      };

      // Initial check after 1 second
      setTimeout(checkElementsAndStartTour, 1000);
    }
  }, [user, shouldShowOnboarding, startupDelay, currentView, chatLoading, conversationId, hasPendingPlan, messages.length]);

  // Function to check if all todos are complete
  const checkTodosComplete = async () => {
    if (!conversationId) return;

    // Don't show modal if we just switched from todos view
    // (user already saw the modal there)
    if (justSwitchedFromTodos) {
      setJustSwitchedFromTodos(false);
      return;
    }

    // Don't show modal if user has already seen it for this completion
    // (prevents re-showing after they dismiss or click "Set New Goal")
    if (hasSeenCompletionModal) {
      return;
    }

    // Don't show modal if there's a pending plan (user is reviewing a new plan)
    const hasPendingPlan = messages.length > 0 &&
      messages[messages.length - 1].content?.includes('<TodoPlan:');
    if (hasPendingPlan) {
      // console.log('[App] Pending plan detected, skipping celebration modal');
      return;
    }

    try {
      const todos = await todosAPI.getTodos(conversationId);

      // Flatten all todos from all stages
      const allTodos = todos.flatMap(stage => stage.todos || []);

      // Get all non-deleted required todos
      const requiredTodos = allTodos.filter(todo => !todo.is_optional && !todo.is_deleted);

      // Check if all required todos are completed
      const allComplete = requiredTodos.length > 0 && requiredTodos.every(todo => todo.is_completed);

      if (allComplete && currentView === VIEWS.CHAT) {
        // console.log('[App] All todos complete! Showing celebration modal');
        setShowCompletionModal(true);
        setHasSeenCompletionModal(true);

        // Reset the flag after 5 minutes so modal can appear again
        setTimeout(() => {
          // console.log('[App] 5 minutes passed, allowing completion modal to show again');
          setHasSeenCompletionModal(false);
        }, 5 * 60 * 1000);
      } else if (!allComplete && hasSeenCompletionModal) {
        // If todos are no longer all complete (new plan approved), reset the flag
        // console.log('[App] Todos not all complete - resetting completion modal flag');
        setHasSeenCompletionModal(false);
      }
    } catch (err) {
      console.error('[App] Failed to check todos completion:', err);
    }
  };

  // Check if session is joinable - use server-provided isJoinable flag
  useEffect(() => {
    if (!upcomingSession) {
      setCanJoinSession(false);
      return;
    }

    // Use server-calculated isJoinable flag
    setCanJoinSession(upcomingSession.isJoinable || false);
  }, [upcomingSession]);

  // Handler to open session details modal
  const handleSessionIndicatorClick = () => {
    setShowSessionDetails(true);
  };

  // Handler for rescheduling a session
  const handleReschedule = async () => {
    if (!upcomingSession) return;

    try {
      // Cancel the current session
      await sessionsAPI.cancelSession(upcomingSession.id);

      // Navigate to scheduling screen with the same coach
      setSelectedCoachForScheduling(upcomingSession.coach);
      setUpcomingSession(null);
      navigateTo(VIEWS.COACHING_SCHEDULE, { coach: upcomingSession.coach });
    } catch (err) {
      console.error('[App] Failed to reschedule session:', err);
      alert('Failed to reschedule session. Please try again.');
    }
  };

  // Handler for canceling a session
  const handleCancel = async () => {
    if (!upcomingSession) return;

    const confirmed = window.confirm('Are you sure you want to cancel this coaching session?');
    if (!confirmed) return;

    try {
      await sessionsAPI.cancelSession(upcomingSession.id);
      setUpcomingSession(null);
      // Inform the chat
      sendMessage('I just cancelled my upcoming coaching session.');
    } catch (err) {
      console.error('[App] Failed to cancel session:', err);
      alert('Failed to cancel session. Please try again.');
    }
  };

  // Tour control handlers
  const handleStartTour = () => {
    setIsMenuOpen(false); // Close hamburger menu
    setTimeout(() => setRunTour(true), 300); // Start tour after menu closes
  };

  const handleJoyrideCallback = (data) => {
    const { status, type, index, action, lifecycle } = data;

    console.log('[Tour] Joyride callback:', {
      status,
      type,
      index,
      action,
      lifecycle,
      step: tourSteps[index]?.target
    });

    // Handle errors when target element doesn't exist
    if (status === 'error' || type === 'error:target_not_found') {
      console.warn('[Tour] Target not found, skipping step:', tourSteps[index]?.target);
      // Tour will automatically skip to next step
      return;
    }

    // Open hamburger menu when reaching step 3 (feedback button step) to prepare for notification/install steps
    if (type === 'step:after' && index === 3 && action === 'next') {
      console.log('[Tour] Opening hamburger menu for notification/install steps');
      setIsMenuOpen(true);

      // Wait for menu to render, then scroll to notification button
      setTimeout(() => {
        const notificationBtn = document.querySelector('[data-tour="enable-notifications"]');
        if (notificationBtn) {
          console.log('[Tour] Scrolling to notification button');
          notificationBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          console.warn('[Tour] Notification button not found');
        }
      }, 400);
    }

    // Close menu when moving past last hamburger step or going back before feedback button
    // Last step is index 5 if install exists, or index 4 if it doesn't
    const lastHamburgerStepIndex = tourSteps.length - 1;
    if ((type === 'step:after' && index === lastHamburgerStepIndex && action === 'next') ||
        (type === 'step:after' && index === 3 && action === 'prev')) {
      console.log('[Tour] Closing hamburger menu');
      setIsMenuOpen(false);
    }

    // Close hamburger menu when going from hamburger step (2) to feedback step (3)
    if (type === 'step:after' && index === 2 && action === 'next') {
      console.log('[Tour] Closing hamburger menu for feedback button step');
      setIsMenuOpen(false);
    }

    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      // Tour ended (completed or skipped)
      console.log('[Tour] Tour ended with status:', status);
      setRunTour(false);
      setIsMenuOpen(false); // Ensure menu is closed when tour ends
      try {
        localStorage.setItem('sophia_tour_completed', 'true');
      } catch (e) {
        console.warn('Could not save tour completion status');
      }
    }
  };

  // Track App Opened and identify user in Mixpanel (only once per session)
  useEffect(() => {
    const identifyUserAndTrack = async () => {
      if (user && !startupDelay && !hasTrackedAppOpened.current) {
        try {
          // Fetch user stats for enhanced profile
          const userStats = await contentAPI.getStats();

          // Build complete user properties with stats
          const userProperties = mixpanelService.buildUserProperties(
            {
              email: user.email,
              name: user.name,
              thinkific_id: user.thinkificId,
              financial_experience: user.financialExperience,
              money_stage: user.moneyStage,
              age_range: user.ageRange,
              countries: user.countries,
              monthly_savings: user.monthlySavings,
              currentMission: user.currentMission,
              createdAt: user.createdAt,
            },
            {
              days_active: userStats.daysActive,
              total_sessions: userStats.totalSessions,
              journey_start_date: userStats.journeyStartDate,
              last_active_date: userStats.lastActiveDate,
              engagement_trend: userStats.engagementTrend,
              todos_completed: userStats.todosCompleted,
              articles_read: userStats.articlesRead,
              videos_watched: userStats.videosWatched,
            }
          );

          // Identify user in Mixpanel with full profile including stats
          mixpanelService.identify(user.email, userProperties);

          // Track App Opened (only once per session)
          mixpanelService.trackEvent('App Opened', {
            has_mission: !!user.currentMission,
            financial_experience: user.financialExperience,
            money_stage: user.moneyStage,
            journey_stage: userProperties.journey_stage,
            days_active: userStats.daysActive,
          });

          hasTrackedAppOpened.current = true;
        } catch (error) {
          console.error('[App] Failed to fetch user stats for Mixpanel:', error);

          // Fall back to basic identification without stats
          mixpanelService.identify(user.email, {
            name: user.name || user.email,
            email: user.email,
            signup_method: 'thinkific_sso',
            thinkific_id: user.thinkificId,
            financial_experience: user.financialExperience,
            money_stage: user.moneyStage,
            age_range: user.ageRange,
            countries: user.countries,
            monthly_savings: user.monthlySavings,
          });

          mixpanelService.trackEvent('App Opened', {
            has_mission: !!user.currentMission,
            financial_experience: user.financialExperience,
            money_stage: user.moneyStage,
          });

          hasTrackedAppOpened.current = true;
        }
      }
    };

    identifyUserAndTrack();
  }, [user, startupDelay]);

  // Track Screen Viewed when view changes (only when view actually changes)
  useEffect(() => {
    if (user && !startupDelay && currentView) {
      const screenNameMap = {
        [VIEWS.CHAT]: 'Chat',
        [VIEWS.TODOS]: 'Todo List',
        [VIEWS.ARTICLE_DETAIL]: 'Article Detail',
        [VIEWS.NEWS_DETAIL]: 'News Detail',
        [VIEWS.ALL_RESOURCES]: 'All Resources',
        [VIEWS.ALL_NEWS]: 'All News',
        [VIEWS.PROGRESS_DASHBOARD]: 'Progress Dashboard',
        [VIEWS.COACHING_SCHEDULE]: 'Coaching Schedule',
        [VIEWS.COACH_DASHBOARD]: 'Coach Dashboard',
        [VIEWS.ORG_DASHBOARD]: 'Organization Dashboard',
        [VIEWS.DEV_DASHBOARD]: 'Dev Dashboard',
      };

      const screenName = screenNameMap[currentView] || 'Unknown';

      // Only track if the view has actually changed
      if (lastTrackedView.current !== screenName) {
        mixpanelService.trackEvent('Screen Viewed', {
          screen_name: screenName,
          previous_screen: lastTrackedView.current || 'none',
        });
        lastTrackedView.current = screenName;
      }
    }
  }, [user, startupDelay, currentView]);

  // Load history when not showing onboarding
  useEffect(() => {
    if (user && !startupDelay && !shouldShowOnboarding) {
      // Check if there's a pending plan (change-objective scenario)
      const hasPendingPlan = messages.length > 0 && messages[messages.length - 1]?.content?.includes('<TodoPlan:');

      if (hasPendingPlan) {
        console.log('[App] Pending plan detected - starting change-objective flow');
        startOnboarding('change-objective');
      } else if (messages.length === 0) {
        // No messages yet, load history
        loadHistory();
      }
    }
  }, [user, startupDelay, shouldShowOnboarding, messages, loadHistory, startOnboarding]);

  // CRITICAL: Validate that user has an active action plan
  // If no plan exists, clear currentMission to trigger onboarding
  useEffect(() => {
    const validateActionPlan = async () => {
      // Only validate if:
      // 1. User is authenticated
      // 2. Onboarding not currently showing
      // 3. We have a conversation ID
      // 4. Not currently loading chat
      if (!user || shouldShowOnboarding || !conversationId || chatLoading) {
        return;
      }

      // Check if there's a pending plan in the last message
      const hasPendingPlan = messages.length > 0 && messages[messages.length - 1]?.content?.includes('<TodoPlan:');
      if (hasPendingPlan) {
        // User has a pending plan to review, that's fine
        return;
      }

      try {
        // Check if user has any active todos
        const todos = await todosAPI.getTodos(conversationId);
        const allTodos = todos.flatMap(stage => stage.todos || []);
        const activeTodos = allTodos.filter(todo => !todo.is_deleted && !todo.mission_changed);

        if (activeTodos.length === 0 && user.currentMission) {
          // NO ACTIVE TODOS but currentMission is set - inconsistent state
          // SOLUTION: Clear currentMission to trigger onboarding
          console.warn('[App] No active plan found but currentMission exists. Clearing currentMission to reset state.');

          try {
            // Clear currentMission from server
            await updateUser({ currentMission: '' });
            console.log('[App] currentMission cleared successfully. Onboarding will show on next render.');
          } catch (err) {
            console.error('[App] Failed to clear currentMission:', err);
          }
        }
      } catch (err) {
        console.error('[App] Failed to validate action plan:', err);
        // Don't block user on error
      }
    };

    validateActionPlan();
  }, [user, shouldShowOnboarding, conversationId, messages, updateUser, chatLoading]);

  // Check if todos are complete when messages are loaded (app open or after sending)
  // ONLY in chat view - TodoList handles its own completion modal
  useEffect(() => {
    if (conversationId && messages.length > 0 && !chatLoading && currentView === VIEWS.CHAT) {
      // Small delay to ensure todos are synced
      const timer = setTimeout(() => {
        checkTodosComplete();
      }, 500);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, messages.length, chatLoading, currentView]);

  // Show startup loading screen
  if (startupDelay) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingText}>SOPHIA</div>
        <div className={styles.loadingSubtext}>Loading...</div>
      </div>
    );
  }

  // Only show loading screen if we're verifying an existing token (not during login)
  if (authLoading && !user) {
    // Check if we have a token - if so, we're verifying it
    const hasToken = localStorage.getItem('authToken');
    if (hasToken) {
      return (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingText}>SOPHIA</div>
          <div className={styles.loadingSubtext}>Authenticating...</div>
        </div>
      );
    }
  }

  if (!user) {
    // Check if we're in development (localhost)
    const isDevelopment = window.location.hostname.includes('localhost') ||
                          window.location.hostname.includes('127.0.0.1');

    // Auto-redirect to Thinkific OAuth login (production only)
    if (!authLoading && !isDevelopment) {
      // Hardcode the server URL to ensure correct redirect in production
      const serverUrl = process.env.REACT_APP_API_URL || 'https://sophia-server-production.up.railway.app';
      const authUrl = `${serverUrl}/api/auth/oauth/initiate`;

      // Only redirect once - check if we're already being redirected
      if (!window.location.href.includes('/api/auth/oauth')) {
        // console.log('[App] No user found, redirecting to OAuth:', authUrl);
        window.location.href = authUrl;
      }

      return (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingText}>SOPHIA</div>
          <div className={styles.loadingSubtext}>Redirecting to login...</div>
        </div>
      );
    }

    return (
      <div className={styles.app}>
        <LoginForm onLogin={login} isLoading={authLoading} />
      </div>
    );
  }

  // Show onboarding if needed
  if (user && shouldShowOnboarding) {
    return (
      <>
        <OnboardingScreen
          user={user}
          flowItems={flowItems}
          flowType={flowType}
          onComplete={async () => {
            console.log('[App] Onboarding completed - refreshing data');
            console.log('[App] Current conversationId BEFORE refresh:', conversationId);
            completeOnboarding();
            // Hide ProgressBar immediately to prevent showing stale data
            setHideProgressBar(true);
            // CRITICAL: Invalidate all caches to fetch fresh data from new conversation
            stateManager.invalidate('messages');
            stateManager.invalidate('todos');
            stateManager.invalidate('progressData');
            stateManager.invalidate('conversationId');
            stateManager.invalidate('currentMission');
            // Wait for loadHistory to fetch new conversation data
            const newConversationId = await loadHistory();
            console.log('[App] New conversationId from loadHistory:', newConversationId);
            console.log('[App] Current conversationId in state (may not be updated yet):', conversationId);
            // Wait for React state updates to propagate
            await new Promise(resolve => setTimeout(resolve, 300));
            // THEN force ProgressBar to remount with the new conversationId
            setProgressBarKey(prev => prev + 1);
            // Show ProgressBar again with fresh data
            setHideProgressBar(false);
            console.log('[App] ProgressBar refresh complete - should now show new data');
          }}
          onDismiss={() => {
            console.log('[App] Onboarding dismissed - user canceled change-objective flow');
            completeOnboarding();
            // User canceled - don't change their objective, just go back to previous view
            // (This could be chat or todos depending on where they started the flow)
          }}
          onUpdateUser={updateUser}
        />
        {/* Feedback Modal */}
        <FeedbackModal
          isOpen={isFeedbackOpen}
          onClose={() => setIsFeedbackOpen(false)}
        />
        {/* Floating Feedback Button - Also available during onboarding */}
        <FeedbackButton onClick={() => setIsFeedbackOpen(true)} />
      </>
    );
  }

  // console.log('[App] Rendering main app');

  // Handle returning from content (send reading time to LLM)
  const handleBackFromContent = () => {
    if (readingSession) {
      const { contentType, title, startTime } = readingSession;
      const durationMs = Date.now() - startTime;
      const durationSeconds = Math.floor(durationMs / 1000);

      // Only send message if they spent at least 3 seconds (avoid accidental clicks)
      if (durationSeconds >= 3) {
        // Categorize engagement level based on duration
        let engagementDescription;

        if (durationSeconds < 20) {
          engagementDescription = 'did not read (closed quickly, likely not interested)';
        } else if (durationSeconds <= 35) {
          engagementDescription = 'skimmed (spent enough time to get the main idea)';
        } else {
          engagementDescription = 'actually read (spent significant time engaging with the content)';
        }

        const message = `[User just finished reading "${title}" (${contentType}) for ${durationSeconds} seconds - ${engagementDescription}]`;
        sendMessage(message);
      }

      setReadingSession(null);
    }
    goBack();
  };

  // Handle returning from screens (Action Plan, Progress Dashboard)
  const handleBackFromScreen = async () => {
    if (visitingScreen) {
      const { screenType, startTime } = visitingScreen;
      const durationMs = Date.now() - startTime;
      const durationSeconds = Math.floor(durationMs / 1000);

      // Only send context if they spent at least 3 seconds
      if (durationSeconds >= 3) {
        if (screenType === 'action-plan') {
          // Fetch current todos to report on status
          try {
            const response = await fetch(`/api/todos?conversationId=${conversationId}`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const tiers = await response.json();

            // Flatten all todos from all tiers
            const allTodos = tiers.flatMap(tier => tier.todos || []);

            // Calculate summary
            const completedCount = allTodos.filter(t => t.is_completed && !t.is_optional).length;
            const totalRequired = allTodos.filter(t => !t.is_optional && !t.is_deleted).length;

            // Find current tier (lowest tier with incomplete required todos)
            let currentTier = 1;
            for (const tier of tiers) {
              const hasIncomplete = tier.todos.some(t => !t.is_completed && !t.is_optional && !t.is_deleted);
              if (hasIncomplete) {
                currentTier = tier.tier_number;
                break;
              }
            }

            // Find next action (first incomplete required todo in current tier)
            const nextAction = allTodos.find(t => !t.is_completed && !t.is_optional && !t.is_deleted)?.todo_text;

            const message = `[User just reviewed their Action Plan. Status: ${completedCount}/${totalRequired} required tasks complete, currently on Stage ${currentTier}${nextAction ? `, next action: "${nextAction}"` : ''}]`;
            sendMessage(message);
          } catch (error) {
            console.error('[App] Failed to fetch todos for context:', error);
          }
        } else if (screenType === 'progress-dashboard') {
          // Fetch journey insights to report on last 2 observations
          try {
            const response = await fetch('/api/progress/journey-insights', {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const insightsData = await response.json();

            const insights = insightsData.insights || [];
            const lastTwo = insights.slice(-2);

            if (lastTwo.length > 0) {
              const insightsSummary = lastTwo.map(i => `Stage ${i.stage}: "${i.insight}"`).join('; ');
              const message = `[User just reviewed their Progress Dashboard. Recent journey insights: ${insightsSummary}]`;
              sendMessage(message);
            } else {
              const message = `[User just reviewed their Progress Dashboard. ${insights.length === 0 ? 'No journey insights yet - they are just getting started.' : ''}]`;
              sendMessage(message);
            }
          } catch (error) {
            console.error('[App] Failed to fetch journey insights for context:', error);
          }
        }
      }

      setVisitingScreen(null);
    }
    goBack();
  };

  // Render view based on currentView
  const renderView = () => {
    switch (currentView) {
      case VIEWS.TODOS:
        return (
          <TodoList
            conversationId={conversationId}
            onBackToChat={() => {
              handleBackFromScreen();
              setJustSwitchedFromTodos(true);
            }}
            onChangePlan={() => {
              goBack(); // Navigate away from todos view first
              startOnboarding('change-objective');
            }}
          />
        );

      case VIEWS.PROGRESS_DASHBOARD:
        return (
          <ProgressDashboard
            onBack={handleBackFromScreen}
            onNavigateToTasks={() => navigateTo(VIEWS.TODOS)}
          />
        );

      case VIEWS.ARTICLE_DETAIL:
        return (
          <ArticleDetailView
            slug={viewData.slug}
            onBack={handleBackFromContent}
          />
        );

      case VIEWS.NEWS_DETAIL:
        return (
          <NewsDetailView
            newsId={viewData.newsId}
            onBack={handleBackFromContent}
          />
        );

      case VIEWS.ALL_ARTICLES:
        return (
          <AllLearningResourcesView
            onBack={goBack}
            onArticleClick={(slug, metadata) => {
              // No tracking from library - only track content from chat recommendations
              navigateTo(VIEWS.ARTICLE_DETAIL, { slug });
            }}
            onVideoClick={(video) => {
              // Open video in new tab - no tracking (opens externally)
              const targetUrl = video.lessonUrl || video.videoUrl;
              if (targetUrl) {
                window.open(targetUrl, '_blank', 'noopener,noreferrer');
              }
            }}
          />
        );

      case VIEWS.ALL_NEWS:
        return (
          <AllNewsView
            onBack={goBack}
            onNewsClick={(newsId, metadata) => {
              // No tracking from library - only track content from chat recommendations
              navigateTo(VIEWS.NEWS_DETAIL, { newsId });
            }}
          />
        );

      case VIEWS.COACHING_SCHEDULE:
        return (
          <SchedulingScreen
            coach={selectedCoachForScheduling || viewData.coach}
            onBack={() => {
              setSelectedCoachForScheduling(null);
              goBack();
            }}
            onBookingComplete={(session) => {
              setUpcomingSession(session);
              setSelectedCoachForScheduling(null);
              goBack();
              // Send a message to the chat about the booking with coach name and formatted time
              const coachName = session.coach?.displayName || 'a coach';
              sendMessage(`I just scheduled a coaching session with ${coachName} for ${formatSessionTime(session.scheduled_start)}`);
              // Scroll to SessionBanner after a short delay to show the banner
              setTimeout(() => {
                const banner = document.querySelector('[class*="SessionBanner"]');
                if (banner) {
                  banner.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 300);
            }}
          />
        );

      case VIEWS.COACH_DASHBOARD:
        return (
          <CoachDashboard
            onBack={goBack}
          />
        );

      case VIEWS.ORG_DASHBOARD:
        return (
          <OrganizationDashboard
            organizationId={user?.adminOfOrganizations?.[0]?.id}
            organizationName={user?.adminOfOrganizations?.[0]?.name}
            onBack={goBack}
          />
        );

      case VIEWS.DEV_DASHBOARD:
        return (
          <DevDashboard
            onBack={goBack}
          />
        );

      case VIEWS.SETTINGS:
        return (
          <SettingsScreen
            user={user}
            onUpdateUser={updateUser}
            onLogout={logout}
            onClose={goBack}
          />
        );

      case VIEWS.CHAT:
      default:
        return (
          <>
            {/* Hide PWA install prompt when join banner is showing */}
            {isInstallable && !canJoinSession && (
              <PWAInstallPrompt
                onInstall={promptInstall}
                onDismiss={dismissPrompt}
                isIOS={isIOS}
              />
            )}
            <Header
              onOpenMenu={() => setIsMenuOpen(true)}
              upcomingSession={upcomingSession}
              onSessionIndicatorClick={handleSessionIndicatorClick}
              hideSessionIndicator={canJoinSession}
            />
            {conversationId && !hasPendingPlan && !hideProgressBar && (
              <ProgressBar
                key={progressBarKey}
                conversationId={conversationId}
                onNavigateToTodos={() => navigateTo(VIEWS.TODOS)}
                onNavigateToDashboard={() => navigateTo(VIEWS.PROGRESS_DASHBOARD)}
                onTodoComplete={(response) => {
                  // Invalidate caches if stage was unlocked
                  if (response.stageUnlocked) {
                    stateManager.invalidate('messages');
                    stateManager.invalidate('todos');
                    stateManager.invalidate('progressData');
                    loadHistory();
                  }
                }}
              />
            )}
            {/* Show join banner only when session is joinable (within ±10 minutes) */}
            {upcomingSession && canJoinSession && (
              <SessionBanner
                session={upcomingSession}
                onJoin={(session) => {
                  // Open Zoom meeting in new tab
                  if (session.zoom_join_url) {
                    window.open(session.zoom_join_url, '_blank', 'noopener,noreferrer');
                  }
                }}
                onDismiss={() => setUpcomingSession(null)}
              />
            )}
            <ChatArea
              messages={messages}
              isLoading={chatLoading}
              conversationId={conversationId}
              currentMission={currentMission}
              loadingMessage={loadingMessage}
              onPlanEdit={(message) => {
                // User wants to edit the plan - send the message to continue conversation
                if (message) {
                  sendMessage(message);
                }
              }}
              onArticleClick={(slug, metadata) => {
                // Start tracking reading session
                setReadingSession({
                  contentType: 'article',
                  title: metadata?.title || 'an article',
                  startTime: Date.now()
                });
                navigateTo(VIEWS.ARTICLE_DETAIL, { slug });
              }}
              onNewsClick={(newsId, metadata) => {
                // Start tracking reading session
                setReadingSession({
                  contentType: 'news',
                  title: metadata?.headline || 'a news article',
                  startTime: Date.now()
                });
                navigateTo(VIEWS.NEWS_DETAIL, { newsId });
              }}
              onCoachClick={(coach) => {
                // Navigate to scheduling screen with selected coach
                setSelectedCoachForScheduling(coach);
                navigateTo(VIEWS.COACHING_SCHEDULE, { coach });
              }}
              onQueuedFactChange={(hasQueuedFactNow) => {
                setHasQueuedFact(hasQueuedFactNow);
              }}
            />
            {!hasPendingPlan && !chatLoading && !hasQueuedFact && (
              <InputSection
                onSendMessage={(msg) => {
                  sendMessage(msg);
                }}
                isLoading={chatLoading}
              />
            )}
            {showNotificationBanner && (
              <NotificationPermissionModal
                onAllow={() => {
                  console.log('[App] Allow button clicked');
                  requestNotificationPermission();
                }}
                onDismiss={() => {
                  console.log('[App] Dismiss button clicked');
                  dismissNotificationBanner();
                }}
              />
            )}
            <TodoCompletionModal
              isOpen={showCompletionModal}
              onClose={() => setShowCompletionModal(false)}
              onSetNewGoal={() => {
                setShowCompletionModal(false);
                // Send message to LLM to find new objective
                sendMessage("I want to find a new financial objective");
              }}
            />
          </>
        );
    }
  };

  // Main render (only reached after onboarding is complete)
  return (
    <div className={styles.app}>
      {renderView()}
      {/* Hamburger menu should never show during onboarding */}
      {!shouldShowOnboarding && (
        <HamburgerMenu
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          onNavigateTasks={() => {
            setIsMenuOpen(false);
            setVisitingScreen({
              screenType: 'action-plan',
              startTime: Date.now(),
              data: null
            });
            navigateTo(VIEWS.TODOS);
          }}
          onNavigateProgress={() => {
            setIsMenuOpen(false);
            setVisitingScreen({
              screenType: 'progress-dashboard',
              startTime: Date.now(),
              data: null
            });
            navigateTo(VIEWS.PROGRESS_DASHBOARD);
          }}
          onNavigateArticles={() => {
            setIsMenuOpen(false);
            navigateTo(VIEWS.ALL_ARTICLES);
          }}
          onNavigateNews={() => {
            setIsMenuOpen(false);
            navigateTo(VIEWS.ALL_NEWS);
          }}
          onNavigateCoachDashboard={() => {
            setIsMenuOpen(false);
            navigateTo(VIEWS.COACH_DASHBOARD);
          }}
          onNavigateOrgDashboard={() => {
            setIsMenuOpen(false);
            navigateTo(VIEWS.ORG_DASHBOARD);
          }}
          onNavigateDevDashboard={() => {
            setIsMenuOpen(false);
            navigateTo(VIEWS.DEV_DASHBOARD);
          }}
          onFeedback={() => {
            setIsMenuOpen(false);
            setIsFeedbackOpen(true);
          }}
          onStartTour={handleStartTour}
          onNavigateSettings={() => {
            setIsMenuOpen(false);
            navigateTo(VIEWS.SETTINGS);
          }}
          user={user}
        />
      )}
      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
      />
      {/* Floating Feedback Button */}
      <FeedbackButton onClick={() => setIsFeedbackOpen(true)} />
      <NotificationToast
        notification={inAppNotification}
        onClose={clearInAppNotification}
        duration={5000}
      />
      {/* Queued Fact Modal - Shows prominently when user has an encouraging fact */}
      {showQueuedFactModal && user && !shouldShowOnboarding && (
        <QueuedFactModal
          onDismiss={() => setShowQueuedFactModal(false)}
        />
      )}
      {/* Session Details Modal - Always show when opened */}
      {showSessionDetails && (
        <SessionDetailsModal
          session={upcomingSession}
          onClose={() => setShowSessionDetails(false)}
          onJoin={(session) => {
            // Open Zoom meeting in new tab
            if (session.zoom_join_url) {
              window.open(session.zoom_join_url, '_blank', 'noopener,noreferrer');
            }
            setShowSessionDetails(false);
          }}
          onReschedule={() => {
            setShowSessionDetails(false);
            handleReschedule();
          }}
          onCancel={() => {
            setShowSessionDetails(false);
            handleCancel();
          }}
          onBookNew={() => {
            setShowSessionDetails(false);
            // Navigate to coach selection - send message to trigger booking flow
            sendMessage('I want to book a coaching session. Please show me the best coach for me immediately without checking if I already have a session.');
          }}
        />
      )}
      {/* Onboarding Tour */}
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        scrollToFirstStep
        disableScrolling={false}
        spotlightClicks
        callback={handleJoyrideCallback}
        floaterProps={{
          disableAnimation: false,
          hideArrow: false,
        }}
        styles={{
          options: {
            zIndex: 250, // Above hamburger (200), below feedback modal (300)
            primaryColor: '#1b4942', // Primary green
            textColor: '#1b4942',
            backgroundColor: '#ffffff',
            arrowColor: '#ffffff',
          },
          tooltip: {
            borderRadius: 12,
            fontSize: 15,
          },
          tooltipTitle: {
            fontFamily: 'Georgia, serif',
            fontSize: 20,
            fontWeight: 600,
            color: '#1b4942',
          },
          tooltipContent: {
            padding: '12px 0',
            lineHeight: 1.5,
            color: '#4a5f5a',
          },
          buttonNext: {
            backgroundColor: '#1b4942',
            borderRadius: 8,
            padding: '8px 16px',
          },
          buttonBack: {
            color: '#6e8e87',
            marginRight: 8,
          },
          buttonSkip: {
            color: '#6e8e87',
          },
        }}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          skip: 'Skip',
        }}
      />
      {/* Update Notification - Shows when new app version is available */}
      <UpdateNotification />
    </div>
  );
}

export default App;
